import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { GOOGLE_REVIEW_URL } from '../utils/brand';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';


function ReviewSubmission() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(!!bookingId);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRating, setSubmittedRating] = useState(0);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [review, setReview] = useState('');
  const [clientName, setClientName] = useState('');
  const [company, setCompany] = useState('');

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
    }
    // Pre-fill from logged-in user
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (user) {
        setClientName(user.name || '');
      }
    } catch (e) {}
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const res = await axios.get(`${API_URL}/property-info/appointment/${bookingId}`);
      if (res.data.appointment) {
        setBooking(res.data.appointment);
        setClientName(res.data.appointment.clientName || '');
      }
    } catch (e) {
      console.log('Could not load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!rating) return;
    if (!review.trim()) return;
    if (!clientName.trim()) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/reviews`, {
        bookingId: bookingId || undefined,
        rating,
        title,
        review,
        clientName,
        company
      }, { headers });

      setSubmittedRating(rating);
      setSubmitted(true);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '80px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // After submission — smart routing
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: '120px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '560px', margin: '0 auto', padding: '0 1.5rem' }}>
          {submittedRating >= 4 ? (
            // High rating — route to Google
            <div style={{ background: 'white', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
              <h1 style={{ fontSize: '1.75rem', color: '#111827', margin: '0 0 0.75rem' }}>Thank You!</h1>
              <p style={{ color: '#6b7280', fontSize: '1.05rem', lineHeight: 1.6, margin: '0 0 2rem' }}>
                We're thrilled you had a great experience! Your review has been submitted to our site.
              </p>

              <div style={{
                background: 'linear-gradient(135deg, #fef9c3, #fef3c7)', border: '2px solid #f59e0b',
                borderRadius: '12px', padding: '1.5rem', margin: '0 0 1.5rem'
              }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⭐⭐⭐⭐⭐</div>
                <h3 style={{ color: '#92400e', margin: '0 0 0.5rem', fontSize: '1.1rem' }}>
                  Would you share your experience on Google?
                </h3>
                <p style={{ color: '#78350f', margin: '0 0 1rem', fontSize: '0.9rem' }}>
                  It takes 30 seconds and helps other agents find us. We'd really appreciate it!
                </p>
                <a
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block', background: '#f59e0b', color: 'white',
                    padding: '14px 36px', borderRadius: '8px', textDecoration: 'none',
                    fontWeight: '700', fontSize: '1rem', transition: 'background 0.2s'
                  }}
                >
                  Leave a Google Review →
                </a>
              </div>

              <Link to="/" style={{ color: '#6b7280', fontSize: '0.9rem' }}>Return to home</Link>
            </div>
          ) : (
            // Low rating — private thank you, no Google prompt
            <div style={{ background: 'white', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🙏</div>
              <h1 style={{ fontSize: '1.75rem', color: '#111827', margin: '0 0 0.75rem' }}>Thank You for Your Feedback</h1>
              <p style={{ color: '#6b7280', fontSize: '1.05rem', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
                We appreciate your honest feedback. Your review has been submitted and our team will review it carefully. We're always working to improve.
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.9rem', margin: '0 0 2rem' }}>
                If there's anything specific we can do better, please don't hesitate to reach out to us directly at{' '}
                <a href="mailto:info@utahrealtyphotos.com" style={{ color: '#2563eb' }}>info@utahrealtyphotos.com</a>.
              </p>
              <Link to="/" style={{ color: '#2563eb', fontWeight: '600', textDecoration: 'none' }}>Return to home</Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Review form
  return (
    <div style={{ minHeight: '100vh', paddingTop: '120px', background: '#f8fafc' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '2.5rem 2rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <h1 style={{ fontSize: '1.75rem', color: '#111827', margin: '0 0 0.25rem' }}>Share Your Experience</h1>
          <p style={{ color: '#6b7280', margin: '0 0 2rem' }}>
            {booking ? `Your shoot at ${booking.propertyAddress}` : 'How was your photography session?'}
          </p>

          {/* Star Rating */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>Rating *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    fontSize: '2.5rem', transition: 'transform 0.15s',
                    color: star <= (hoverRating || rating) ? '#f59e0b' : '#d1d5db',
                    transform: star <= (hoverRating || rating) ? 'scale(1.15)' : 'scale(1)'
                  }}
                >
                  ★
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '6px 0 0' }}>
                {rating === 5 ? 'Excellent!' : rating === 4 ? 'Great!' : rating === 3 ? 'Good' : rating === 2 ? 'Fair' : 'Poor'}
              </p>
            )}
          </div>

          {/* Name */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>Your Name *</label>
            <input
              type="text"
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              placeholder="Jane Smith"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box' }}
            />
          </div>

          {/* Company */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>Company / Brokerage</label>
            <input
              type="text"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="ABC Realty (optional)"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box' }}
            />
          </div>

          {/* Title */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>Review Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Outstanding photos, quick turnaround!"
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box' }}
            />
          </div>

          {/* Review */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem' }}>Your Review *</label>
            <textarea
              value={review}
              onChange={e => setReview(e.target.value)}
              rows={5}
              placeholder="Tell us about your experience — the photos, the photographer, turnaround time, anything that stood out..."
              style={{ width: '100%', padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }}
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting || !rating || !review.trim() || !clientName.trim()}
            style={{
              width: '100%', padding: '14px', background: '#2563eb', color: 'white',
              border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '1rem',
              cursor: (submitting || !rating || !review.trim() || !clientName.trim()) ? 'not-allowed' : 'pointer',
              opacity: (submitting || !rating || !review.trim() || !clientName.trim()) ? 0.6 : 1
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReviewSubmission;
